/**
 * Owner: Masa Bayu
 * File: PrivateAuthorTopBar.tsx
 * Date:Time: 2026-10-23 09:05
 * Description: Topbar dashboard PRIVAT §467-C — carian menghantar TEKARAN SEBENAR
 *   ke /search?q= (sebelum ini pertanyaan dibuang); loceng hanya bernombot bila
 *   pelayan melaporkan pesanan yang benar-benar menunggu (tiada lencana 3
 *   tetap); avatar inisial + nama daripada sesi penerbit, bukan potret demo.
 */

import { useState } from 'react';
import type { FormEvent, ReactElement, ReactNode } from 'react';
import type { PrivateAuthorLabels } from './private-author-labels';
import type { AuthorDashboardModel } from './private-author-view';

function Stroke({
  children,
  size = 20,
}: {
  readonly children: ReactNode;
  readonly size?: number;
}): ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export interface PrivateAuthorTopBarProps {
  readonly onNavigate: (path: string) => void;
  readonly onShowNotifications: () => void;
  readonly la: PrivateAuthorLabels;
  readonly model: AuthorDashboardModel;
}

export function PrivateAuthorTopBar({
  onNavigate,
  onShowNotifications,
  la,
  model,
}: PrivateAuthorTopBarProps): ReactElement {
  const [query, setQuery] = useState('');

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    const needle = query.trim();
    onNavigate(needle.length === 0 ? '/search' : `/search?q=${encodeURIComponent(needle)}`);
  };

  const badge = model.alertCount;

  return (
    <header className="qxp__topbar">
      <form className="qxp__search" role="search" onSubmit={submit} aria-label={la.searchSubmit}>
        <span className="qxp__search-icon" aria-hidden="true">
          <Stroke size={18}>
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-4.2-4.2" />
          </Stroke>
        </span>
        <input
          type="search"
          value={query}
          placeholder={la.searchPlaceholder}
          aria-label={la.searchSubmit}
          onChange={(event) => {
            setQuery(event.target.value);
          }}
        />
      </form>
      <div className="qxp__topbar-actions">
        <button
          type="button"
          className="qxp__iconbtn"
          aria-label={
            badge > 0
              ? `${la.notificationsAria} (${String(badge)})`
              : `${la.notificationsAria} (${la.noData})`
          }
          onClick={onShowNotifications}
        >
          <Stroke>
            <path d="M6 9a6 6 0 0 1 12 0c0 5 2 6 2 6H4s2-1 2-6" />
            <path d="M10 19a2 2 0 0 0 4 0" />
          </Stroke>
          {badge > 0 ? (
            <span className="qxp__badge" aria-hidden="true">
              {String(badge)}
            </span>
          ) : null}
        </button>
        <a
          className="qxp__iconbtn"
          href="mailto:support@qxread.com?subject=Support%20chat"
          aria-label={la.chatAria}
        >
          <Stroke>
            <path d="M21 12a8 8 0 0 1-8 8H8l-5 3 1.5-5A8 8 0 1 1 21 12z" />
            <path d="M8.5 12h.01M12 12h.01M15.5 12h.01" />
          </Stroke>
        </a>
        <button
          type="button"
          className="qxp__account"
          aria-label={la.accountAria}
          onClick={() => {
            onNavigate('/author/profile');
          }}
        >
          <span className="qxp__account-avatar" aria-hidden="true">
            {model.initials}
          </span>
          <span className="qxp__account-text">
            <strong>{model.name}</strong>
            <em>{la.brand}</em>
          </span>
        </button>
      </div>
    </header>
  );
}
